import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { 
  Landmark, 
  Calendar, 
  Coins, 
  Plus, 
  Loader2 
} from "lucide-react";
import Modal from "../../../components/admin/Modal";
import { useToast } from "../../../components/admin/ToastContext";
import { get, post } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

function toNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

const ESSLoansPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const [amortization, setAmortization] = useState([]);
  const [loanHistory, setLoanHistory] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ amount: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
   const [activeLoan, setActiveLoan] = useState(null);
   const [interestRate, setInterestRate] = useState(null);
   const [eligibility, setEligibility] = useState(null);

  const { user } = useSelector((state) => state.auth);

  const loadData = useCallback(async () => {
    try {
      const empRes = await get("/api/employees?limit=500");
      const allEmployees = empRes?.data || [];
      const currentEmp = allEmployees.find((e) => e.work_email === user?.email) || null;
      const empId = currentEmp?.id || currentEmp?.employee_id;
      setEmployeeId(empId);
      if (!empId) { setLoaded(true); return; }

      const loansRes = await get(`/api/loans?employee_id=${empId}&limit=200`);
      const loans = loansRes?.data || [];

      const active = (loans || []).find(l => l.state === "approve" || l.state === "approved" || l.state === "APPROVED");
      setActiveLoan(active);

      if (active) {
        setInterestRate(active.interest_rate != null ? active.interest_rate : active.rate != null ? active.rate : null);
      }

      if (!active && currentEmp) {
        const monthlyIncome = currentEmp.salary || currentEmp.monthly_income || 0;
        const elig = monthlyIncome > 0 ? monthlyIncome * 3 : currentEmp.loan_eligibility != null ? currentEmp.loan_eligibility : null;
        setEligibility(elig);
      }

      if (active) {
        const linesRes = await get(`/api/loans/${active.id}/lines?limit=60`);
        const lines = linesRes?.data || [];
        const amort = (lines || []).map((l, i) => {
          const amountNum = toNum(l.amount);
          return {
            month: i + 1,
            due: l.date ? new Date(l.date).toLocaleDateString(intl.locale) : "—",
            amount: amountNum.toLocaleString(intl.locale) + " XAF",
            amountNum,
            principal: Math.round(amountNum * 0.88).toLocaleString(intl.locale) + " XAF",
            interest: Math.round(amountNum * 0.12).toLocaleString(intl.locale) + " XAF",
            balance: "—",
            status: l.paid ? "paid" : "upcoming",
          };
        });
        setAmortization(amort);

        setLoanHistory([
          { event: intl.formatMessage({ id: "ess.loans.eventApproved" }), date: new Date(active.date || active.date_applied || Date.now()).toLocaleDateString(intl.locale), color: "bg-emerald-500" },
          { event: intl.formatMessage({ id: "ess.loans.eventSubmitted" }), date: "—", color: "bg-amber-400" },
        ]);
        setLoaded(true);
        return;
      }

      if (loans && loans.length > 0) {
        setLoanHistory((loans || []).map(l => ({
          event: intl.formatMessage({ id: "ess.loans.eventLoan" }, { name: l.name || "" }),
          date: l.date ? new Date(l.date).toLocaleDateString(intl.locale) : l.date_applied ? new Date(l.date_applied).toLocaleDateString(intl.locale) : "—",
          color: l.state === "approve" || l.state === "approved" || l.state === "APPROVED" ? "bg-emerald-500" : l.state === "refuse" || l.state === "refused" || l.state === "REFUSED" ? "bg-rose-500" : "bg-amber-400",
        })));
        setLoaded(true);
        return;
      }
    } catch (_) {}

    setAmortization([]);
    setLoanHistory([]);
    setLoaded(true);
  }, [intl, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const paidCount = amortization.filter((a) => a.status === "paid").length;
  const totalCount = amortization.length;
  const paidTotal = amortization.filter((a) => a.status === "paid").reduce((s, a) => s + a.amountNum, 0);
  const monthlyAmount = amortization[0]?.amountNum
    ? amortization[0].amountNum.toLocaleString(intl.locale) + " XAF"
    : activeLoan
      ? (toNum(activeLoan.monthly_installment) || (toNum(activeLoan.loan_amount) / Math.max(toNum(activeLoan.installment), 1))).toLocaleString(intl.locale) + " XAF"
      : "—";

  const handleNewLoan = async () => {
    if (!form.amount || !form.reason) {
      addToast(intl.formatMessage({ id: "ess.common.fillAllFields" }), "error");
      return;
    }
    setSubmitting(true);
    try {
      await post("/api/loans", {
        name: form.reason.trim(),
        employee_id: employeeId,
        loan_type: "personal",
        loan_amount: parseFloat(form.amount) || 0,
        date_applied: new Date().toISOString().split("T")[0],
      });
      addToast(intl.formatMessage({ id: "ess.loans.submitSuccess" }), "success");
      setSubmitting(false);
      setModalOpen(false);
      setForm({ amount: "", reason: "" });
      window.location.reload();
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.common.submitError" }), "error");
      setSubmitting(false);
    }
  };

  if (!loaded) return <PageSkeleton />;

  const nextDue = amortization.find((a) => a.status === "upcoming")?.due || "—";

  const stats = [
    { labelId: "ess.loans.activeLoan", value: activeLoan ? `1 (${monthlyAmount})` : "0", sub: activeLoan ? intl.formatMessage({ id: "ess.loans.active" }) : intl.formatMessage({ id: "ess.loans.none" }), icon: Landmark, color: "text-emerald-600 bg-emerald-100" },
    { labelId: "ess.loans.monthlyDeduction", value: monthlyAmount, sub: intl.formatMessage({ id: "ess.loans.nextDue" }, { date: nextDue }), icon: Calendar, color: "text-blue-600 bg-blue-100" },
    { labelId: "ess.loans.totalRepaid", value: paidTotal ? `${paidTotal.toLocaleString(intl.locale)} XAF` : "0 XAF", sub: intl.formatMessage({ id: "ess.loans.monthsPaid" }, { paid: paidCount, total: totalCount }), icon: Coins, color: "text-emerald-600 bg-emerald-100" },
    { labelId: "ess.loans.eligibility", value: activeLoan ? "—" : (eligibility != null ? `${Math.round(eligibility).toLocaleString()} XAF` : "—"), sub: activeLoan ? intl.formatMessage({ id: "ess.loans.inProgress" }) : intl.formatMessage({ id: "ess.loans.available" }), icon: Landmark, color: "text-amber-600 bg-amber-100" },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {intl.formatMessage({ id: "ess.loans.title" })}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {intl.formatMessage({ id: "ess.loans.subtitle" })}
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-sm"
        >
          <Plus size={16} /> {intl.formatMessage({ id: "ess.loans.newLoan" })}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, sub, icon: Icon, color }) => (
          <div key={labelId} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: labelId })}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-400 mt-1">{sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">{intl.formatMessage({ id: "ess.loans.activeTitle" })}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{activeLoan ? intl.formatMessage({ id: "ess.loans.salaryAdvance" }, { id: activeLoan.id }) : intl.formatMessage({ id: "ess.loans.noActiveLoan" })}</p>
              </div>
              {activeLoan && (
                <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-700 border-emerald-100">{intl.formatMessage({ id: "ess.loans.active" })}</span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              {[
                { label: intl.formatMessage({ id: "ess.loans.amount" }), value: activeLoan ? toNum(activeLoan.loan_amount).toLocaleString(intl.locale) + " XAF" : "—" },
                { label: intl.formatMessage({ id: "ess.loans.tenure" }), value: activeLoan ? intl.formatMessage({ id: "ess.loans.months" }, { count: activeLoan.installment || totalCount || 6 }) : "—" },
                 { label: intl.formatMessage({ id: "ess.loans.rate" }), value: interestRate != null ? `${interestRate}%` : "—" },
                { label: intl.formatMessage({ id: "ess.loans.monthly" }), value: monthlyAmount },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-sm font-semibold text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">{intl.formatMessage({ id: "ess.loans.schedule" })}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.loans.colMonth" })}</th>
                    <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.loans.colDue" })}</th>
                    <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.loans.amount" })}</th>
                    <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden sm:table-cell">{intl.formatMessage({ id: "ess.loans.colPrincipal" })}</th>
                    <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden md:table-cell">{intl.formatMessage({ id: "ess.loans.colInterest" })}</th>
                    <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden sm:table-cell">{intl.formatMessage({ id: "ess.loans.colBalance" })}</th>
                    <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.loans.colStatus" })}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {amortization.length === 0 ? (
                    <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-400">{intl.formatMessage({ id: "ess.loans.noSchedule" })}</td></tr>
                  ) : (
                    amortization.map((row) => (
                      <tr key={row.month} className="hover:bg-gray-50/50">
                        <td className="px-5 py-3.5 font-semibold text-gray-900">{row.month}</td>
                        <td className="px-5 py-3.5 text-gray-700">{row.due}</td>
                        <td className="px-5 py-3.5 font-medium text-gray-900">{row.amount}</td>
                        <td className="px-5 py-3.5 text-gray-700 hidden sm:table-cell">{row.principal}</td>
                        <td className="px-5 py-3.5 text-gray-600 hidden md:table-cell">{row.interest}</td>
                        <td className="px-5 py-3.5 text-gray-700 hidden sm:table-cell">{row.balance}</td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${row.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-50 text-slate-600 border-slate-100"}`}>
                            {row.status === "paid" ? intl.formatMessage({ id: "ess.loans.paid" }) : intl.formatMessage({ id: "ess.loans.upcoming" })}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">{intl.formatMessage({ id: "ess.loans.history" })}</h3>
          <div className="space-y-0">
            {loanHistory.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">{intl.formatMessage({ id: "ess.loans.noHistory" })}</p>
            ) : (
              loanHistory.map((item, i) => (
                <div key={i} className="flex gap-3 pb-4 last:pb-0 relative">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full ${item.color} ring-2 ring-white mt-1.5`} />
                    {i < loanHistory.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                  </div>
                  <div>
                    <p className="text-sm text-gray-700">{item.event}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.date}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100">
            <button onClick={() => setModalOpen(true)} className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-800 font-medium">
              <Plus size={14} className="mr-1" /> {intl.formatMessage({ id: "ess.loans.newRequest" })}
            </button>
          </div>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={intl.formatMessage({ id: "ess.loans.modalTitle" })}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.loans.amountLabel" })}</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              placeholder={intl.formatMessage({ id: "ess.loans.amountPlaceholder" })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.loans.reasonLabel" })}</label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none"
              placeholder={intl.formatMessage({ id: "ess.loans.reasonPlaceholder" })}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setModalOpen(false)} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">{intl.formatMessage({ id: "ess.common.cancel" })}</button>
            <button onClick={handleNewLoan} disabled={submitting} className="flex-1 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50">{submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> {intl.formatMessage({ id: "ess.common.sending" })}</> : intl.formatMessage({ id: "ess.common.submit" })}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ESSLoansPage;
